import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { MessageService } from '../../services/message.service';
import { LoginComponent } from './login/login.component';
import { ILoginUser } from './new_user.interface';
import { ITokens } from './tokens.interface';
import { Role } from '../../enums/role.enum';
import { environment } from '../../shared/environments/environment';
import { Params } from '@angular/router';
import { PersonService } from '../../services/person.service';
import { Person } from '../../classes/person.class';
import { IPerson } from '../../typing/person.interface';

@Injectable({ providedIn: 'root' })
export class AuthService {
    loggedIn = signal(false);
    user = signal(new Person({} as IPerson));
    isAdmin = signal(false);
    isManager = signal(false);
    isExecutor = signal(false);
    companyId = signal(0);

    registrationUrl = `${environment.API_BASE_PATH}/auth/registration`;
    authUrl = `${environment.API_BASE_PATH}/auth/login`;

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private userService: PersonService,
        private messageService: MessageService,
    ) {
        if (this.getAccessToken()) {
            this.checkRole(String(this.getAccessToken()));
            this.loggedIn.set(true);
        }
    }

    authCheck(): void {
        if (!this.loggedIn()) {
            this.dialog.open(LoginComponent, {
                width: '100%',
                maxWidth: '400px',
                hasBackdrop: true,
                disableClose: true,
                data: {
                    authService: this,
                },
            });
            return;
        }

        this.getCurrentUser();
    }

    registration(newUserData: ILoginUser): void {
        this.http.post(this.registrationUrl, newUserData).subscribe({
            error: err => this.messageService.errorMessage(err.error.message),
        });
    }

    logIn(userData: ILoginUser): void {
        delete userData.confirmedPassword;
        // Determine if the current domain matches executorApp's domain
        const isExecutorApp = window.location.origin.includes('localhost:4201')
        || window.location.origin.includes('executor.j-call.ru')
        || window.location.origin.includes('executor-dev.j-call.ru'); //Добавить еще и домен дев и мастер приложения executor
        const query: Params =isExecutorApp ? {executorApp: true} : {};
        this.http.post<ITokens>(this.authUrl, userData, {params: query}).subscribe({
            next: (data: ITokens) => {
                if (this.getRoleFromToken(data.accessToken) === Role.admin) {
                    this.isAdmin.set(true);
                }

                this.loggedIn.set(true);
                this.saveToLocalStorage(data);
                window.location.reload();
            },
            error: err => this.messageService.errorMessage(err.error.message),
        });
    }

    saveToLocalStorage(data: ITokens): void {
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
    }

    getAccessToken(): string | null {
        return localStorage.getItem('accessToken');
    }

    getRefreshToken(): string | null {
        return localStorage.getItem('refreshToken');
    }

    logout(): void {
        this.loggedIn.set(false);
        this.clearCache().then(() => {
            window.location.reload();
        });
    }

    private async clearCache(): Promise<void> {
        localStorage.clear();
        sessionStorage.clear();

        if ('caches' in window) {
            const cacheNames = await window.caches.keys();
            await Promise.all(cacheNames.map(name => window.caches.delete(name)));
        }

        return Promise.resolve();
    }

    getRoleFromToken(token: string): string | null {
        const tokenParts = token.split('.');
        const decodedPayload = atob(tokenParts[1]);
        const payload = JSON.parse(decodedPayload);
        return payload.role;
    }

    getCurrentUser(): void {
        this.userService.currentUser().subscribe({
            next: (data: Person) => this.user.set(data),
            error: () => this.messageService.errorMessage('Ошибка авторизации'),
        });
    }

    refreshUserToken(body: { refreshToken: string }): Observable<ITokens> {
        return this.http.post<ITokens>(`${environment.API_BASE_PATH}/auth/refresh`, body);
    }

    checkRole(accessToken: string): void {
        switch (this.getRoleFromToken(accessToken)) {
        case Role.executor:
            this.isExecutor.set(true);
            break;
        case Role.admin:
            this.isAdmin.set(true);
            break;
        case Role.manager:
            this.isManager.set(true);
            break;
        default:
            this.messageService.errorMessage('Неизвестная роль');
            break;
        }
    }
}
